export type ZoneColorOption = {
	id: string;
	overlay: ZoneColorOverlay;
	/** Persisted on zone.color and shown in the color picker. */
	value: string;
};

export type ZoneColorOverlay = {
	/** Token 400 — brighter fill/line on the night basemap. */
	dark: string;
	/** Token 500 — fill/line on the light basemap. */
	light: string;
};

/**
 * Canonical zone palette: picker swatches and map overlay light/dark pairs stay in sync.
 * Distinct pairs from the Untitled UI utility scale (400 / 500).
 */
export const ZONE_COLOR_OPTIONS: ZoneColorOption[] = [
	{
		id: "brand",
		overlay: { dark: "#36BFFA", light: "#0BA5EC" },
		value: "#0BA5EC",
	},
	{
		id: "purple",
		overlay: { dark: "#9B8AFB", light: "#7A5AF8" },
		value: "#7A5AF8",
	},
	{
		id: "success",
		overlay: { dark: "#47CD89", light: "#17B26A" },
		value: "#17B26A",
	},
	{
		id: "orange",
		overlay: { dark: "#F38744", light: "#EF6820" },
		value: "#EF6820",
	},
	{
		id: "pink",
		overlay: { dark: "#F670C7", light: "#EE46BC" },
		value: "#EE46BC",
	},
	{
		id: "indigo",
		overlay: { dark: "#8098F9", light: "#6172F3" },
		value: "#6172F3",
	},
	{
		id: "teal",
		overlay: { dark: "#2ED3B7", light: "#15B79E" },
		value: "#15B79E",
	},
	{
		id: "fuchsia",
		overlay: { dark: "#E478FA", light: "#D444F1" },
		value: "#D444F1",
	},
];

export const DEFAULT_ZONE_COLOR = ZONE_COLOR_OPTIONS[0]!.value;

export const ZONE_COLOR_SWATCHES = ZONE_COLOR_OPTIONS.map(
	(option) => option.value,
);

const normalizeHexColor = (color: string): string => color.trim().toUpperCase();

export const normalizeZoneColor = (color?: string): string =>
	color?.trim() || DEFAULT_ZONE_COLOR;

export const findZoneColorOption = (
	color?: string,
): undefined | ZoneColorOption => {
	if (!color?.trim()) return undefined;

	const normalized = normalizeHexColor(color);
	return ZONE_COLOR_OPTIONS.find(
		(option) => normalizeHexColor(option.value) === normalized,
	);
};

/** Map fill/line colors for a stored or fallback zone hex. */
export const resolveZoneOverlayColors = (color?: string): ZoneColorOverlay => {
	const matched = findZoneColorOption(color);
	if (matched) return matched.overlay;

	const fallback = normalizeHexColor(normalizeZoneColor(color));
	if (/^#[0-9A-F]{6}$/.test(fallback)) {
		return { dark: fallback, light: fallback };
	}

	return ZONE_COLOR_OPTIONS[0]!.overlay;
};

export const mergeZoneColorSwatches = (extraColors: string[]): string[] => {
	const seen = new Set<string>();
	const merged: string[] = [];

	for (const color of [...ZONE_COLOR_SWATCHES, ...extraColors]) {
		const normalized = normalizeHexColor(color);
		if (!/^#[0-9A-F]{6}$/.test(normalized) || seen.has(normalized)) continue;
		seen.add(normalized);
		merged.push(normalized);
	}

	return merged;
};
